import type { AppProps } from "next/app";
import Head from "next/head";
import "react-perfect-scrollbar/dist/css/styles.css";
import "tailwindcss/tailwind.css";
import "../styles/globals.scss";
import { Provider } from "react-redux";
import { useRouter } from "next/router";
import { ToastContainer } from "react-toastify";
import ReactTooltip from "react-tooltip";
import "react-toastify/dist/ReactToastify.css";
import "rsuite/dist/rsuite-no-reset.min.css";
import { PersistGate } from "redux-persist/integration/react";
import { QueryClient, QueryClientProvider, setLogger } from "react-query";
import "swiper/css";
import "swiper/css/pagination";
import { useEffect } from "react";
import Gleap from "gleap";
import * as Sentry from "@sentry/nextjs";
import { store, persistor } from "../Store/Store";
import { setBreadcrumbRoute } from "../Store/Slices/MasterSlice";

setLogger({
  log: () => {},
  warn: () => {},
  error: () => {},
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 0,
    },
    mutations: {
      retry: 0,
    },
  },
});

if (process.env.NEXT_PUBLIC_SENTRY_CHECK === "true") {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    // integrations: [new Sentry.BrowserTracing()],
    tracesSampleRate: 1.0,
    environment: process.env.NEXT_PUBLIC_SENTRY_ENV,
  });
}

function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  useEffect(() => {
    Gleap.initialize(process.env.NEXT_PUBLIC_GLEAP_KEY || "");
  }, []);

  const clarityData = `(function(c,l,a,r,i,t,y){
    c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
    t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
    y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
})(window, document, "clarity", "script", "j2ot9mq4mo");`;

  useEffect(() => {
    store.dispatch(
      setBreadcrumbRoute({
        path: router?.pathname,
        query_path: router?.asPath,
      }),
    );
  }, [router]);

  return (
    <Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <Head>
              <title>Intuions</title>
              <meta name="viewport" content="initial-scale=1.0, width=device-width" />
              {process.env.NEXT_PUBLIC_CLARITY_CHECK === "true" && (
                <script defer async dangerouslySetInnerHTML={{ __html: clarityData }} />
              )}
            </Head>
            <Component {...pageProps} />
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              theme="colored"
            />
            <ReactTooltip aria-haspopup="true" effect="solid" globalEventOff="click" />
          </PersistGate>
        </Provider>
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
}

export default App;
